<template>
    <StatisticsLayout :title="$t('statistics.title')">
        <template #summary>
            <div class="grid grid-cols-2 gap-x-4" data-testid="statistics-total-games">
                <StatsHeroTile
                    :title="$t('statistics.total_games')"
                    :value="statisticsStore.totalGames()"
                    :footnote="
                        $t('statistics.vs_last_month', {
                            delta: statisticsStore.gamesVsLastMonth(),
                        })
                    "
                />
                <StatsHeroTile
                    variant="light"
                    :title="$t('statistics.hours_played')"
                    :value="
                        $t('statistics.hours_value', {
                            hours: hours(statisticsStore.allTime?.seconds_played),
                        })
                    "
                    :footnote="
                        $t('statistics.hours_in_month', {
                            hours: hours(statisticsStore.months.at(-2)?.seconds_played),
                            month: lastMonthName,
                        })
                    "
                />
            </div>
        </template>

        <section class="flex flex-col gap-4">
            <h2 class="font-title text-base leading-4 font-extrabold uppercase">
                {{ $t('statistics.game_mode_stats') }}
            </h2>
            <nav class="flex flex-col gap-2">
                <router-link
                    v-for="mode in modes"
                    :key="mode.id"
                    :to="{ name: mode.routeName }"
                    :data-testid="`statistics-mode-${mode.id}`"
                    class="group block"
                >
                    <Panel
                        size="full"
                        padding="p-4"
                        :corners="false"
                        background-color="bg-card group-hover:bg-card-2"
                    >
                        <div class="flex flex-col gap-4 tracking-[0.25px]">
                            <div class="flex items-start justify-between gap-4">
                                <span class="text-base leading-6 font-black uppercase">{{
                                    mode.label
                                }}</span>
                                <div class="flex flex-col items-end gap-2">
                                    <DonutChart
                                        :percentage="mode.winrate"
                                        :has-games="mode.hasDecidedGames"
                                        class="size-6"
                                    />
                                    <span class="text-xs leading-4">
                                        {{
                                            $t('statistics.winrate_short', {
                                                percentage: mode.winrate,
                                            })
                                        }}
                                    </span>
                                </div>
                            </div>
                            <div class="flex items-center justify-between gap-4 text-sm leading-5">
                                <span class="capitalize">{{
                                    $t('statistics.games_count', mode.games)
                                }}</span>
                                <span class="text-titles">{{ playedAgo(mode.matchtypeKey) }}</span>
                            </div>
                        </div>
                    </Panel>
                </router-link>
            </nav>
        </section>

        <!-- Setup stats are for X01 (the profile stats tab's default game type) until the design
             settles which game type the statistics home should show. -->
        <section class="flex flex-col gap-4">
            <div class="flex items-center justify-between gap-4">
                <h2 class="font-title text-base leading-4 font-extrabold uppercase">
                    {{ $t('statistics.setup_stats') }}
                </h2>
                <SelectDrawer v-model="selectedStat" :presets="statPresets" class="w-40">
                    {{ $t('statistics.filter_setup_stats') }}
                </SelectDrawer>
            </div>

            <SetupStatsCard
                v-for="(setup, index) in setupCards"
                :key="setup.id"
                :setup="setup"
                :rank="index + 1"
                class="cursor-pointer"
                data-testid="setup-card"
                @click="openSetup(setup)"
            />
        </section>
    </StatisticsLayout>
</template>

<script>
import { mapStores } from 'pinia'
import { useStatisticsStore } from '@/stores/statistics.js'
import { useAuthStore } from '@/stores/auth.js'
import { useDartSetupStore } from '@/stores/dartSetup.js'
import StatisticsLayout from '@/components/statistics/StatisticsLayout.vue'
import StatsHeroTile from '@/components/statistics/StatsHeroTile.vue'
import DonutChart from '@/components/statistics/DonutChart.vue'
import SelectDrawer from '@/components/ui/SelectDrawer.vue'
import SetupStatsCard from '@/components/statistics/SetupStatsCard.vue'
import Panel from '@/components/layout/Panel.vue'

export default {
    name: 'StatisticsView',
    components: {
        StatisticsLayout,
        StatsHeroTile,
        DonutChart,
        SelectDrawer,
        SetupStatsCard,
        Panel,
    },
    data() {
        return {
            selectedStat: 'winrate',
        }
    },
    computed: {
        ...mapStores(useStatisticsStore, useAuthStore, useDartSetupStore),

        modes() {
            return [
                {
                    id: 'x01',
                    label: this.$t('statistics.mode_x01'),
                    routeName: 'statistics-x01',
                    matchtypeKey: 'matchtype_501',
                },
                {
                    id: 'segments',
                    label: this.$t('statistics.mode_segments'),
                    routeName: 'statistics-segments',
                    matchtypeKey: 'matchtype_segments',
                },
                {
                    id: 'tactics',
                    label: this.$t('statistics.mode_tactics'),
                    routeName: 'statistics-tactics',
                    matchtypeKey: 'matchtype_tactics',
                },
            ].map((mode) => {
                const { wins = 0, losses = 0 } =
                    this.statisticsStore.allTime?.mode_stats?.[mode.matchtypeKey]?.winrate ?? {}
                return {
                    ...mode,
                    games: this.statisticsStore.totalGames(mode.matchtypeKey),
                    hasDecidedGames: wins + losses > 0,
                    winrate: wins + losses > 0 ? Math.round((wins / (wins + losses)) * 100) : 0,
                }
            })
        },

        lastMonthName() {
            const now = new Date()
            return new Date(now.getFullYear(), now.getMonth() - 1, 1).toLocaleDateString(
                this.$i18n.locale,
                {
                    month: 'long',
                },
            )
        },

        statPresets() {
            return [
                { label: this.$t('player_stats.stat_winrate'), value: 'winrate' },
                { label: this.$t('player_stats.stat_wins'), value: 'wins' },
                { label: this.$t('player_stats.stat_games_played'), value: 'games_played' },
                { label: this.$t('player_stats.stat_avg_throw'), value: 'average' },
            ]
        },

        /** The player's setups with their X01 stats, sorted by the picked stat (highest first). */
        setupCards() {
            const setupStats = this.authStore.statistics?.setups || {}
            const recentResults = this.statisticsStore.allTime?.setup_recent_results || {}
            const statOf = (setup) => setupStats[setup.id]?.matchtype_501?.[this.selectedStat] || 0

            const cards = [...(this.authStore.dart_setups || [])]
                .sort((a, b) => statOf(b) - statOf(a))
                .map((setup) => {
                    const stats = setupStats[setup.id]?.matchtype_501 ?? {}
                    const games = stats.games_played ?? 0
                    const wins = stats.wins ?? 0
                    const results = recentResults[setup.id]?.matchtype_501 ?? []
                    return {
                        ...setup,
                        games,
                        wins,
                        losses: Math.max(games - wins, 0),
                        winrate: Math.round(stats.winrate ?? 0),
                        average: games > 0 ? Math.round(stats.average ?? 0) : '-',
                        avgFirstNine: games > 0 ? Math.round(stats.avg_first_nine ?? 0) : '-',
                        recentResults: results,
                    }
                })

            // "Best setup" is the one with the highest winrate among those that have games.
            const best = cards
                .filter((card) => card.games > 0)
                .reduce((top, card) => (!top || card.winrate > top.winrate ? card : top), null)

            return cards.map((card) => ({ ...card, isBest: card.id === best?.id }))
        },
    },
    methods: {
        hours(seconds) {
            return Math.round((seconds ?? 0) / 3600)
        },

        /** "Played today", "Played 2d ago", "Played 1w ago" from the mode's last match. */
        playedAgo(matchtypeKey) {
            const lastPlayedAt =
                this.statisticsStore.allTime?.mode_stats?.[matchtypeKey]?.last_played_at
            if (!lastPlayedAt) {
                return ''
            }

            const startOfDay = (date) =>
                new Date(date.getFullYear(), date.getMonth(), date.getDate())
            const days = Math.round(
                (startOfDay(new Date()) - startOfDay(new Date(lastPlayedAt))) / 86400000,
            )

            if (days <= 0) {
                return this.$t('statistics.played_today')
            }
            if (days < 7) {
                return this.$t('statistics.played_days_ago', { count: days })
            }
            if (days < 30) {
                return this.$t('statistics.played_weeks_ago', { count: Math.floor(days / 7) })
            }
            return this.$t('statistics.played_months_ago', { count: Math.floor(days / 30) })
        },

        openSetup(setup) {
            this.dartSetupStore.setEntryPoint({ type: 'route', name: this.$route.name })
            this.$router.replace({ name: 'setup-detail', params: { id: setup.id } })
        },
    },
}
</script>
